// Copyright (c) 2020-2023 dātma, inc™
//
// Unauthorized copying of this file, via any medium is strictly prohibited
// Proprietary and confidential

import { store } from '@/store'
import { fetchWithTimeout } from '@/common/shared'
import { ehrBaseUrl, ehrCrudUrl, radiologyViewerUrl } from '@/settings'
import { compareDesc } from 'date-fns'

export const getRadiology = async (patientId, patientMrn) => {
  try {
    const fetchParams = {
      method: 'GET',
      headers: {
        'Authorization': 'Bearer ' + store.getters.jwt,
      },
    }

    // collection response
    const collectionUrl = `${ehrBaseUrl}/imaging?patient_id=${patientId}`
    const collectionResponse = await fetchWithTimeout(collectionUrl, fetchParams)
    if (!collectionResponse.ok) {
      console.log(`error: status=${collectionResponse.status}`)
    }

    // get reports
    const reportsUrl = `${ehrCrudUrl}/report-store/patient-identifier/${patientMrn}`
    const reportsResponse = await fetchWithTimeout(reportsUrl, fetchParams)
    if (!reportsResponse.ok) {
      console.log(`error: status=${reportsResponse.status}`)
    }

    if (!collectionResponse.ok && !reportsResponse.ok) { return [] }

    const collectionResult = await collectionResponse.json()
    const reportsResult = await reportsResponse.json()
    const filteredReports = reportsResult.reports.filter(r => r.report_type.short_name === 'RADIOLOGY')


    const sortedResults = Object.keys(collectionResult.date_activated).map(key => ({
      date: collectionResult.date_activated[key] || null,
      // Get first block of text from the description string
      imagingType: (collectionResult.description[key] && /^[A-Z]+/.exec(collectionResult.description[key]) || [null])[0],
      description: collectionResult.description[key] || null,
      accessionNumber: collectionResult.accession_number[key] || null,
    }))


    const mergedResult = [...sortedResults, ...filteredReports]

    const dateLookup = {}
    mergedResult.forEach((r) => {
      if (r.date) { dateLookup[r.date] = new Date(r.date) }
      if (r.ordering_date) { dateLookup[r.ordering_date] = new Date(r.ordering_date) }
    })
    mergedResult.sort((a, b) => {
      const dateA = a.date ? dateLookup[a.date] : dateLookup[a.ordering_date]
      const dateB = b.date ? dateLookup[b.date] : dateLookup[b.ordering_date]

      return compareDesc(dateA, dateB)
    })

    const formattedResult = mergedResult.map(normalizeRadiologyResult)
    return formattedResult
  } catch (e) {
    if (e.name === 'AbortError') {
      console.log('timeout error')
    } else {
      console.log('ERROR:', e)
    }
  }
  return []
}

const normalizeRadiologyResult = (radiologyResult) => {
  const formatResult = {
    date: radiologyResult.date || radiologyResult.ordering_date,
    imagingType: radiologyResult.imagingType || radiologyResult?.identifiers.find(i => i.identifier_type === 'specimen_type')?.identifier || null,
    accessionNumber: radiologyResult.accessionNumber || radiologyResult?.identifiers?.find(i => i.identifier_type === 'accession_num')?.identifier || null,
    labName: radiologyResult.labName || radiologyResult?.identifiers?.find(i => i.identifier_type === 'lab_name')?.identifier || null,
    testType: radiologyResult.description || null,
    id: radiologyResult.uuid || null,
    mimeType: radiologyResult.mimetype || null,
  }
  return formatResult
}

export const getRadiologyViewerUrl = async (accessionNumber) => {
  if (!radiologyViewerUrl) { return '' }
  const fetchResponse = await fetchWithTimeout(radiologyViewerUrl, {
    method: 'POST',
    contentType: 'application/json',
    headers: {
      'Authorization': 'Bearer ' + store.getters.jwt,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      'accession_number': accessionNumber,
    }),
  })

  const fetchResult = await fetchResponse.json()
  return fetchResult
}
